import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { statutLabels, type StatutCommande } from '../../../mocks/commandes';
import { formatPrice } from '../../../utils/format';

type TabType = 'commandes' | 'stocks' | 'personnel';

interface RapportContentProps {
  activeTab: TabType;
  statutFilter: string;
  searchQuery: string;
  dateDebut?: string;
  dateFin?: string;
  onExportData: (headers: string[], rows: string[][], title: string) => void;
  boutiqueId?: number | 'toutes';
}

export default function RapportContent({ activeTab, statutFilter, searchQuery, dateDebut, dateFin, onExportData, boutiqueId }: RapportContentProps) {
  const q = searchQuery.toLowerCase();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    setLoading(true);
    const debut = dateDebut || '';
    const fin = dateFin || '';
    const boutiqueParam = boutiqueId === 'toutes' ? undefined : Number(boutiqueId);

    if (activeTab === 'commandes') {
      api.getCommandesReport(token, debut, fin, boutiqueParam)
        .then(res => setData(res))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else if (activeTab === 'stocks') {
      api.getStocks(token, boutiqueParam)
        .then(res => setData(res))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else if (activeTab === 'personnel') {
      Promise.all([
        api.getPersonnel(token),
        api.getCommandes(token)
      ])
        .then(([persoRes, cmdRes]) => {
          // Filter commands by boutique if needed
          const filteredCmds = boutiqueParam === undefined
            ? cmdRes
            : cmdRes.filter((c: any) => c.boutique_id === boutiqueParam);

          // Merge commands/affectations stats into personnel
          const enriched = persoRes.map((p: any) => {
            const myAffs = filteredCmds.flatMap((c: any) => (c.affectations || [])).filter((a: any) => String(a.personnel_id) === String(p.id));
            const totalGains = myAffs.reduce((sum: number, a: any) => sum + (parseFloat(a.cout_main_doeuvre) || 0), 0);
            return { ...p, period_count: myAffs.length, period_gains: totalGains };
          });
          setData(enriched);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [activeTab, dateDebut, dateFin, boutiqueId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-foret-200 border-t-foret-600 rounded-full animate-spin mb-4" />
        <p className="text-gray-400 text-sm">Chargement des données...</p>
      </div>
    );
  }

  if (activeTab === 'commandes') {
    const filteredData = data.filter(c => {
      const matchStatut = statutFilter === 'tous' || c.statut === statutFilter;
      const matchSearch = !q || (c.client_nom || '').toLowerCase().includes(q) || (c.id + '').toLowerCase().includes(q);
      return matchStatut && matchSearch;
    });

    const headers = ['Réf.', 'Client', 'Modèle', 'Statut', 'Date cmd', 'Livraison', 'Total Brut FCFA', 'Remise FCFA', 'Total Net FCFA', 'Encaissé FCFA', 'Reste FCFA'];
    const rows = filteredData.map(c => [
      c.id, c.client_nom || 'Client inconnu', c.modele_nom || 'Sur mesure',
      statutLabels[c.statut as StatutCommande] || c.statut, c.date_commande || c.dateCommande, c.date_livraison || c.dateLivraison,
      (c.total || 0).toString(),
      (c.remise || 0).toString(),
      ((c.total || 0) - (c.remise || 0)).toString(),
      (c.acompte || 0).toString(),
      (c.reste || 0).toString(),
    ]);

    const totalBrut = filteredData.reduce((s, c) => s + (parseFloat(c.total) || 0), 0);
    const totalRemise = filteredData.reduce((s, c) => s + (parseFloat(c.remise) || 0), 0);
    const totalEnc = filteredData.reduce((s, c) => s + (parseFloat(c.acompte) || 0), 0);
    const totalReste = filteredData.reduce((s, c) => s + (parseFloat(c.reste) || 0), 0);

    return (
      <div>
        <div className="flex gap-3 mb-4 flex-wrap">
          {[
            { label: 'Commandes', value: filteredData.length, accent: 'text-gray-800' },
            { label: 'CA Brut', value: formatPrice(totalBrut), accent: 'text-gray-600' },
            { label: 'Remises', value: formatPrice(totalRemise), accent: 'text-amber-600' },
            { label: 'CA Net', value: formatPrice(totalBrut - totalRemise), accent: 'text-foret-600' },
            { label: 'Encaissé', value: formatPrice(totalEnc), accent: 'text-green-600' },
            { label: 'Reste à percevoir', value: formatPrice(totalReste), accent: 'text-red-500' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-4 py-2.5 flex-1 min-w-[120px]">
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className={`text-base font-bold ${s.accent}`}>{s.value}</p>
            </div>
          ))}
          <button
            onClick={() => onExportData(headers, rows, 'rapport-commandes')}
            className="px-3 py-2 bg-foret-500 text-white text-xs font-semibold rounded-xl cursor-pointer hover:bg-foret-600 whitespace-nowrap flex items-center gap-1.5 self-start"
          >
            <i className="ri-download-line" /> Exporter
          </button>
        </div>

        {filteredData.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-16 text-center">
            <div className="w-10 h-10 flex items-center justify-center text-gray-300 mb-3"><i className="ri-calendar-close-line text-3xl" /></div>
            <p className="text-sm font-semibold text-gray-500">Aucune commande sur cet intervalle</p>
            <p className="text-xs text-gray-400 mt-1">Modifiez les filtres pour élargir la recherche</p>
          </div>
        )}

        {filteredData.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
            {/* Mobile View: Cards */}
            <div className="md:hidden divide-y divide-gray-50 pb-20">
              {filteredData.map(c => {
                const total = parseFloat(c.total) || 0;
                const remise = parseFloat(c.remise) || 0;
                const acompte = parseFloat(c.acompte) || 0;
                const reste = parseFloat(c.reste) !== undefined ? parseFloat(c.reste) : (total - remise - acompte);
                return (
                  <div key={c.id} className="p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-gray-400">#{c.id}</span>
                        <span className="text-sm font-black text-gray-900 uppercase tracking-tight truncate max-w-[150px]">{c.client_nom || 'Client inconnu'}</span>
                      </div>
                      <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${
                        ({ EN_ATTENTE: 'bg-yellow-50 text-yellow-700 border-yellow-200', EN_COUPE: 'bg-sky-50 text-sky-700 border-sky-200', EN_COUTURE: 'bg-violet-50 text-violet-700 border-violet-200', TERMINE: 'bg-green-50 text-green-700 border-green-200', LIVRE: 'bg-gray-50 text-gray-600 border-gray-200' } as Record<string, string>)[c.statut] || ''
                      }`}>{statutLabels[c.statut as StatutCommande] || c.statut}</span>
                    </div>

                    <div className="flex items-center justify-between mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                       <span>Début: {c.date_commande || c.dateCommande}</span>
                       <span>Fin: {c.date_livraison || c.dateLivraison}</span>
                    </div>

                    <div className="grid grid-cols-4 gap-1 bg-gray-50 rounded-xl p-3">
                       <div className="text-center">
                          <p className="text-[9px] text-gray-400 font-black uppercase">Brut</p>
                          <p className="text-xs font-black text-gray-900">{formatPrice(total, false)}</p>
                       </div>
                       <div className="text-center border-l border-gray-200">
                          <p className="text-[9px] text-gray-400 font-black uppercase">Remise</p>
                          <p className="text-xs font-black text-amber-600">{formatPrice(remise, false)}</p>
                       </div>
                       <div className="text-center border-l border-gray-200">
                          <p className="text-[9px] text-gray-400 font-black uppercase">Payé</p>
                          <p className="text-xs font-black text-green-600">{formatPrice(acompte, false)}</p>
                       </div>
                       <div className="text-center border-l border-gray-200">
                          <p className="text-[9px] text-gray-400 font-black uppercase">Reste</p>
                          <p className={`text-xs font-black ${reste > 0 ? 'text-red-600' : 'text-gray-400'}`}>{formatPrice(reste, false)}</p>
                       </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop View: Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Réf.', 'Client', 'Modèle', 'Statut', 'Date cmd', 'Livraison', 'Total Brut', 'Remise', 'Total Net', 'Encaissé', 'Reste'].map(h => (
                      <th key={h} className="text-left text-[10px] font-bold text-gray-400 uppercase px-4 py-3 whitespace-nowrap tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredData.map(c => {
                    const total = parseFloat(c.total) || 0;
                    const remise = parseFloat(c.remise) || 0;
                    const acompte = parseFloat(c.acompte) || 0;
                    const reste = parseFloat(c.reste) !== undefined ? parseFloat(c.reste) : (total - remise - acompte);
                    return (
                      <tr key={c.id} className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-4 py-3 text-xs font-mono text-gray-400">{c.id}</td>
                        <td className="px-4 py-3">
                           <p className="text-sm font-bold text-gray-800 whitespace-nowrap">{c.client_nom || 'Client inconnu'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                             <div className="w-8 h-8 rounded bg-gray-100 overflow-hidden flex-shrink-0 border border-gray-100">
                               {c.modele_image ? (
                                 <img src={api.getProxyUrl(c.modele_image)} className="w-full h-full object-cover object-top" alt="" />
                               ) : <div className="w-full h-full flex items-center justify-center text-gray-300"><i className="ri-image-line text-[10px]" /></div>}
                             </div>
                             <p className="text-xs text-gray-600 truncate max-w-[100px]">{c.modele_nom || 'Sur mesure'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                            ({ EN_ATTENTE: 'bg-yellow-50 text-yellow-700 border-yellow-200', EN_COUPE: 'bg-sky-50 text-sky-700 border-sky-200', EN_COUTURE: 'bg-violet-50 text-violet-700 border-violet-200', TERMINE: 'bg-green-50 text-green-700 border-green-200', LIVRE: 'bg-gray-50 text-gray-600 border-gray-200' } as Record<string, string>)[c.statut] || ''
                          }`}>{statutLabels[c.statut as StatutCommande] || c.statut}</span>
                        </td>
                        <td className="px-4 py-3 text-[10px] text-gray-500 font-bold whitespace-nowrap uppercase">{c.date_commande || c.dateCommande}</td>
                        <td className="px-4 py-3 text-[10px] text-gray-500 font-bold whitespace-nowrap uppercase">{c.date_livraison || c.dateLivraison}</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-800 whitespace-nowrap">{formatPrice(total, false)}</td>
                        <td className="px-4 py-3 text-sm text-amber-600 font-bold whitespace-nowrap">{formatPrice(remise, false)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-800 whitespace-nowrap">{formatPrice(total - remise, false)}</td>
                        <td className="px-4 py-3 text-sm text-green-600 font-bold whitespace-nowrap">{formatPrice(acompte, false)}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className={reste > 0 ? 'text-red-500 font-bold' : 'text-gray-300 font-medium'}>
                            {formatPrice(reste, false)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (activeTab === 'stocks') {
    const filteredData = data.filter(p => {
      return !q || (p.nom || '').toLowerCase().includes(q) || (p.type || '').toLowerCase().includes(q);
    });

    const headers = ['Produit', 'Type', 'Couleur', 'Stock', 'Unité', 'Seuil alerte', 'Prix unit. FCFA', 'Valeur totale FCFA', 'Statut'];
    const rows = filteredData.map(p => {
      const qte = parseFloat(p.quantite_stock) || 0;
      const pu = parseFloat(p.prix_unitaire) || 0;
      return [
        p.nom, p.type || 'Tissu', p.couleur || 'N/A',
        qte.toString(), p.unite || 'mètres', (p.seuil_alerte || 0).toString(),
        pu.toString(), (qte * pu).toString(),
        qte <= (p.seuil_alerte || 0) ? 'ALERTE' : 'OK',
      ];
    });

    const totalVal = filteredData.reduce((s, p) => s + (parseFloat(p.quantite_stock) || 0) * (parseFloat(p.prix_unitaire) || 0), 0);
    const alertes = filteredData.filter(p => (parseFloat(p.quantite_stock) || 0) <= (p.seuil_alerte || 0)).length;

    return (
      <div>
        <div className="flex gap-3 mb-4 flex-wrap">
          {[
            { label: 'Références', value: data.length, accent: 'text-gray-800' },
            { label: 'Valeur totale', value: formatPrice(totalVal), accent: 'text-foret-600' },
            { label: 'Alertes stock', value: alertes, accent: alertes > 0 ? 'text-red-500' : 'text-green-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-4 py-2.5 flex-1 min-w-[140px]">
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className={`text-base font-bold ${s.accent}`}>{s.value}</p>
            </div>
          ))}
          <button
            onClick={() => onExportData(headers, rows, 'rapport-stocks')}
            className="px-3 py-2 bg-foret-500 text-white text-xs font-semibold rounded-xl cursor-pointer hover:bg-foret-600 whitespace-nowrap flex items-center gap-1.5 self-start"
          >
            <i className="ri-download-line" /> Exporter
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          {/* Mobile View: Cards */}
          <div className="md:hidden divide-y divide-gray-50 pb-20">
             {filteredData.map(p => {
                const qte = parseFloat(p.quantite_stock) || 0;
                const pu = parseFloat(p.prix_unitaire) || 0;
                const isAlerte = qte <= (p.seuil_alerte || 0);
                const imageUrl = p.image || p.tissu_image;
                return (
                  <div key={p.id} className={`p-4 ${isAlerte ? 'bg-red-50/20' : 'bg-white'}`}>
                    <div className="flex items-center gap-3 mb-3">
                       <div className="w-10 h-10 rounded-xl overflow-hidden bg-gray-100 border border-gray-100 flex-shrink-0">
                          {imageUrl ? (
                            <img src={api.getProxyUrl(imageUrl)} alt={p.nom} className="w-full h-full object-cover object-top" />
                          ) : <div className="w-full h-full flex items-center justify-center text-gray-300"><i className="ri-palette-line text-lg" /></div>}
                       </div>
                       <div className="flex-1 min-w-0">
                          <p className="text-sm font-black text-gray-900 truncate uppercase mt-0.5">{p.nom}</p>
                          <p className="text-[10px] text-gray-400 font-bold truncate uppercase">{p.type || 'Tissu'} · {(p.couleur || 'N/A')}</p>
                       </div>
                       <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${isAlerte ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                          {isAlerte ? 'ALERTE' : 'OK'}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                       <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Stock actuel</p>
                          <p className="text-sm font-black text-gray-900">{qte} <span className="text-[10px] font-bold text-gray-400 uppercase">{p.unite || 'm'}</span></p>
                       </div>
                       <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Valeur estimée</p>
                          <p className="text-sm font-black text-foret-700">{formatPrice(qte * pu, false)}</p>
                       </div>
                    </div>
                  </div>
                );
             })}
          </div>

          {/* Desktop View: Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Produit', 'Type', 'Stock actuel', 'Seuil', 'Prix unit.', 'Valeur totale', 'Statut'].map(h => (
                    <th key={h} className="text-left text-[10px] font-bold text-gray-400 uppercase px-4 py-3 whitespace-nowrap tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredData.map(p => {
                  const qte = parseFloat(p.quantite_stock) || 0;
                  const pu = parseFloat(p.prix_unitaire) || 0;
                  const isAlerte = qte <= (p.seuil_alerte || 0);
                  const imageUrl = p.image || p.tissu_image;
                  return (
                    <tr key={p.id} className={`hover:bg-gray-50/80 transition-colors ${isAlerte ? 'bg-red-50/40' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                           <div className="w-10 h-10 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-100">
                             {imageUrl ? (
                               <img src={api.getProxyUrl(imageUrl)} alt={p.nom} className="w-full h-full object-cover object-top" />
                             ) : <div className="w-full h-full flex items-center justify-center text-gray-300"><i className="ri-palette-line text-lg" /></div>}
                           </div>
                          <div>
                            <p className="text-sm font-bold text-gray-800">{p.nom}</p>
                            <p className="text-xs text-gray-400 font-medium">{p.couleur || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full">{p.type || 'Tissu'}</span></td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-800">{qte} <span className="text-[10px] text-gray-400 uppercase">{p.unite || 'm'}</span></td>
                      <td className="px-4 py-3 text-xs text-gray-400 font-bold uppercase">Seuil: {p.seuil_alerte || 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-medium">{formatPrice(pu, false)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-foret-700">{formatPrice(qte * pu, false)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${isAlerte ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                          {isAlerte ? '⚠ ALERTE' : '✓ OK'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Personnel tab
  const filteredPersonnel = data.filter(p => !q || (p.nom || '').toLowerCase().includes(q));
  const headersPerso = ['Nom', 'Spécialité', 'Commandes', 'Gains FCFA', 'Statut'];
  const rowsPerso = filteredPersonnel.map(p => [
    p.nom,
    p.specialite || p.role || 'Personnel',
    (p.period_count || 0).toString(),
    (p.period_gains || 0).toString(),
    p.actif ? 'Actif' : 'Inactif',
  ]);

  const totalPeriodGains = filteredPersonnel.reduce((s, p) => s + (p.period_gains || 0), 0);
  const totalPeriodCmds = filteredPersonnel.reduce((s, p) => s + (p.period_count || 0), 0);

  return (
    <div>
      <div className="flex gap-3 mb-4 flex-wrap">
        {[
          { label: 'Personnel actif', value: data.filter(p => p.actif).length, accent: 'text-gray-800' },
          { label: 'Total Commandes (Période)', value: totalPeriodCmds, accent: 'text-foret-600' },
          { label: 'Total Main d\'œuvre (Période)', value: formatPrice(totalPeriodGains), accent: 'text-green-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-4 py-2.5 flex-1 min-w-[140px]">
            <p className="text-xs text-gray-400">{s.label}</p>
            <p className={`text-base font-bold ${s.accent}`}>{s.value}</p>
          </div>
        ))}
        <button
          onClick={() => onExportData(headersPerso, rowsPerso, 'rapport-personnel')}
          className="px-3 py-2 bg-foret-500 text-white text-xs font-semibold rounded-xl cursor-pointer hover:bg-foret-600 whitespace-nowrap flex items-center gap-1.5 self-start"
        >
          <i className="ri-download-line" /> Exporter
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        {/* Mobile View: Cards */}
        <div className="md:hidden divide-y divide-gray-50 pb-20">
           {filteredPersonnel.map(p => (
              <div key={p.id} className="p-4 flex items-center justify-between">
                 <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center text-white text-xs font-black">
                       {(p.nom || '??').split(' ').filter(Boolean).map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                       <p className="text-sm font-black text-gray-900 uppercase mt-0.5">{p.nom || 'Inconnu'}</p>
                       <p className="text-[10px] text-gray-400 font-bold uppercase">{p.specialite || p.role || 'Personnel'} · {p.period_count || 0} cmd · {formatPrice(p.period_gains || 0, false)}</p>
                    </div>
                 </div>
                 <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${p.actif ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-400 border-gray-100'}`}>
                    {p.actif ? 'ACTIF' : 'INACTIF'}
                 </span>
              </div>
           ))}
        </div>

        {/* Desktop View: Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['Nom', 'Spécialité', 'Commandes', 'Gains', 'Statut'].map(h => (
                  <th key={h} className="text-left text-[10px] font-bold text-gray-400 uppercase px-4 py-3 whitespace-nowrap tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredPersonnel.map(p => (
                <tr key={p.id} className={`hover:bg-gray-50/80 transition-colors ${!p.actif ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0 bg-foret-500 border border-foret-600 shadow-sm`}>
                        {(p.nom || '??').split(' ').filter(Boolean).map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <span className="text-sm font-bold text-gray-800">{p.nom || 'Inconnu'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200`}>
                      {p.specialite || p.role || 'Personnel'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-black text-gray-800">
                    {p.period_count || 0}
                  </td>
                  <td className="px-4 py-3 text-sm font-black text-foret-700">
                    {formatPrice(p.period_gains || 0, false)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${p.actif ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-400 border-gray-100'}`}>
                      {p.actif ? '● ACTIF' : '○ INACTIF'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
